import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { FileText, Copy, ArrowSquareOut, Sparkle } from "@phosphor-icons/react";

// Reports → "PM Activity Report": property/portfolio + last N days → copyable text + share link
export default function PmActivityReportCard() {
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get("property")?.replace(/-/g, " ") || "");
  const [days, setDays] = useState(Number(params.get("days")) || 7);
  const [recipient, setRecipient] = useState(params.get("to") || "");
  const [busy, setBusy] = useState(false);
  const [rep, setRep] = useState(null);
  const [recent, setRecent] = useState([]);

  const loadRecent = () => api.get("/activity-report/recent").then(r => setRecent(r.data || [])).catch(() => {});
  useEffect(() => { loadRecent(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/activity-report", { query, days, recipient });
      setRep(r.data); loadRecent();
      const c = r.data.counts;
      toast.success(`${c.completed} completed · ${c.active} in progress · ${c.new} new`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not build report"); }
    finally { setBusy(false); }
  };
  const copy = async () => { await navigator.clipboard.writeText(rep.text); toast.success("Report copied — paste into Gmail"); };

  return (
    <div className="rugged-card p-6 bg-white border-[#00C4CC]" data-testid="pm-activity-card">
      <h3 className="font-display font-black text-lg uppercase inline-flex items-center gap-2"><FileText weight="fill" size={18} className="text-[#00C4CC]"/> PM Activity Report</h3>
      <p className="text-xs text-slate-600 font-medium mt-1">What happened at a property or portfolio in the last N days — completed (with photo proof), return visits, in progress, new. Copy → Gmail, or send the share link.</p>
      <div className="mt-3 flex flex-wrap gap-2 items-end">
        <label className="flex-1 min-w-[180px]"><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Property / portfolio / PM</span>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Chew Flats, 5346 Chew Ave, JSCRE…" data-testid="pm-report-query"
            className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm font-bold"/></label>
        <label><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Last</span>
          <select value={days} onChange={e => setDays(Number(e.target.value))} data-testid="pm-report-days" className="mt-1 h-10 px-2 border-2 border-slate-300 text-sm font-bold bg-white">
            {[7, 14, 30, 60, 90].map(d => <option key={d} value={d}>{d} days</option>)}
          </select></label>
        <label className="w-32"><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">To (first name)</span>
          <input value={recipient} onChange={e => setRecipient(e.target.value)} placeholder="Recipient" data-testid="pm-report-recipient"
            className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm font-bold"/></label>
        <button onClick={generate} disabled={busy || query.trim().length < 2} data-testid="pm-report-generate"
          className="tactile-btn h-10 px-4 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50">
          <Sparkle weight="fill" size={14}/> {busy ? "Building…" : "Generate"}
        </button>
      </div>
      {rep && (
        <div className="mt-4" data-testid="pm-report-result">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500" data-testid="pm-report-counts">
              {rep.counts.completed} completed · {rep.counts.returning} returning · {rep.counts.active} in progress · {rep.counts.new} new · {rep.counts.scanned} WOs scanned
            </span>
            <button onClick={copy} data-testid="pm-report-copy" className="ml-auto h-9 px-3 bg-[#00C4CC] text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1"><Copy weight="bold" size={14}/> Copy text</button>
            <a href={rep.share_url} target="_blank" rel="noreferrer" data-testid="pm-report-share" className="h-9 px-3 border-2 border-[#0f172a] font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1"><ArrowSquareOut weight="bold" size={14}/> Share link</a>
          </div>
          <textarea value={rep.text} readOnly rows={12} data-testid="pm-report-text" className="mt-2 w-full p-3 border-2 border-slate-300 text-sm font-medium bg-slate-50"/>
        </div>
      )}
      {recent.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Recent</div>
          <div className="mt-1 divide-y divide-slate-100 border border-slate-200">
            {recent.map(r => (
              <a key={r.token} href={r.share_url} target="_blank" rel="noreferrer" data-testid={`pm-report-recent-${r.token}`} className="flex items-center gap-2 px-3 h-9 text-xs font-bold hover:bg-slate-50">
                <span className="truncate">{r.label} · {r.days}d</span>
                <span className="ml-auto text-slate-500 font-medium flex-shrink-0">{new Date(r.created_at).toLocaleDateString()} · {r.views || 0} views</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
